import { useState, useEffect, useRef } from 'react';

interface DialogProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    closeOnBackdropClick?: boolean;
}

const Dialog = ({
    isOpen,
    onClose,
    children,
    closeOnBackdropClick = true
}: DialogProps) => {
    const [isMounted, setIsMounted] = useState(isOpen);
    const [isVisible, setIsVisible] = useState(false);
    const touchStartYRef = useRef<number | null>(null);
    const timersRef = useRef<{
        close?: NodeJS.Timeout;
        open?: NodeJS.Timeout;
        mount?: NodeJS.Timeout;
    }>({});

    useEffect(() => {
        // Cleanup function
        const cleanup = () => {
            if (timersRef.current.close) clearTimeout(timersRef.current.close);
            if (timersRef.current.open) clearTimeout(timersRef.current.open);
            if (timersRef.current.mount) clearTimeout(timersRef.current.mount);
        };

        cleanup();

        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';

            // Monta il componente (asincrono per evitare warning)
            timersRef.current.mount = setTimeout(() => {
                setIsMounted(true);

                // Triggera l'animazione dopo il mount
                timersRef.current.open = setTimeout(() => {
                    setIsVisible(true);
                }, 10);
            }, 0);
        } else {
            // Nascondi (inizia animazione di chiusura) - asincrono per evitare warning
            setTimeout(() => setIsVisible(false), 0);

            // Smonta dopo l'animazione (300ms)
            timersRef.current.close = setTimeout(() => {
                setIsMounted(false);
                document.body.style.overflow = '';
                document.body.style.touchAction = '';
            }, 300);
        }

        return cleanup;
    }, [isOpen]);

    const handleBackdropClick = () => {
        if (closeOnBackdropClick) {
            onClose();
        }
    };

    // LOGICA DI SWIPE
    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        touchStartYRef.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
        if (touchStartYRef.current === null) return;

        const touchEndY = e.changedTouches[0].clientY;
        const deltaY = touchEndY - touchStartYRef.current;

        // Se lo swipe è verso il basso e supera i 50px, chiama direttamente onClose
        if (deltaY > 50) {
            onClose();
        }

        touchStartYRef.current = null;
    };

    // Non renderizzare se non montato
    if (!isMounted) {
        return null;
    }

    return (
        <div
            className={`
                fixed
                left-0 top-0
                w-screen
                h-dvh 
                z-9999
                flex items-end
                justify-center
                transition-all
                duration-300
                bg-black/50 backdrop-blur-sm
                ${isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}
            `}
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0"
                onClick={handleBackdropClick}
            />

            {/* Dialog Content */}
            <div
                className={`
                    relative
                    w-full
                    bg-base-100
                    rounded-t-3xl
                    shadow-2xl
                    transform
                    transition-transform
                    duration-300
                    max-h-[85vh]
                    flex
                    flex-col
                    ${isVisible ? 'translate-y-0' : 'translate-y-full'}
                `}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className="absolute top-0 left-0 right-0 h-8 flex flex-col items-center pt-3 z-50 cursor-grab active:cursor-grabbing touch-none select-none"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    {/* La barretta visiva */}
                    <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
                </div>
                {children}
            </div>
        </div>
    );
};

export default Dialog;
